(() => {
  "use strict";

  const config = window.CourtCurrent || {};
  const routes = config.routes || {};
  let storeCopy = {};
  try {
    storeCopy = JSON.parse(document.querySelector("[data-store-copy]")?.textContent || "{}");
  } catch (_error) {
    storeCopy = {};
  }
  const liveRegion = document.getElementById("ThemeLiveRegion");
  const COMPARE_KEY = "court-current-compare-v1";
  const RECENT_KEY = "court-current-recent-v1";
  const MAX_COMPARE = 4;
  const focusableSelector =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = "";
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 30);
  }

  function track(eventName, detail = {}) {
    if (config.analyticsEnabled !== true) return;
    const payload = {
      event: eventName,
      currency: config.page?.currency || "USD",
      ...detail,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent("court-current:event", { detail: payload }),
    );
  }

  window.CourtCurrent = {
    ...config,
    track,
  };

  function escapeHTML(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  }

  function money(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: config.page?.currency || "USD",
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function setExpanded(button, open) {
    if (button) button.setAttribute("aria-expanded", String(open));
  }

  function canReceiveFocus(element) {
    if (!element || element.disabled || element.closest('[hidden], [inert]')) return false;
    let ancestor = element;
    while (ancestor && ancestor !== document.body) {
      const style = window.getComputedStyle(ancestor);
      if (style.display === "none" || style.visibility === "hidden") return false;
      if (ancestor.matches('details:not([open])')) {
        const summary = ancestor.querySelector(':scope > summary');
        if (!summary?.contains(element)) return false;
      }
      ancestor = ancestor.parentElement;
    }
    return true;
  }

  function openLayer(layer, trigger, closeSelector, preferredFocus) {
    if (!layer) return;
    const dialog = layer.matches('[role="dialog"]') ? layer : layer.querySelector('[role="dialog"]') || layer;
    const oldRole = dialog.getAttribute("role");
    const oldModal = dialog.getAttribute("aria-modal");
    const oldTabIndex = dialog.getAttribute("tabindex");
    layer.hidden = false;
    layer.dataset.open = "true";
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("tabindex", "-1");
    document.body.classList.add("is-locked");
    setExpanded(trigger, true);
    const inerted = [];
    let branch = layer;
    while (branch.parentElement && branch !== document.body) {
      [...branch.parentElement.children].forEach((sibling) => {
        if (sibling !== branch && !sibling.hasAttribute("inert") && !["SCRIPT", "STYLE", "LINK"].includes(sibling.tagName)) {
          sibling.setAttribute("inert", "");
          inerted.push(sibling);
        }
      });
      branch = branch.parentElement;
    }
    const controls = () => [...dialog.querySelectorAll(focusableSelector)].filter((element) => element.tabIndex >= 0 && canReceiveFocus(element));
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      layer.hidden = true;
      delete layer.dataset.open;
      document.body.classList.remove("is-locked");
      setExpanded(trigger, false);
      inerted.forEach((element) => element.removeAttribute("inert"));
      [["role", oldRole], ["aria-modal", oldModal], ["tabindex", oldTabIndex]].forEach(([name, value]) => {
        if (value === null) dialog.removeAttribute(name);
        else dialog.setAttribute(name, value);
      });
      document.removeEventListener("keydown", onKeydown);
      layer.querySelectorAll(closeSelector).forEach((control) => control.removeEventListener("click", close));
      trigger?.focus();
    };
    const onKeydown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); return; }
      if (event.key !== "Tab") return;
      const focusable = controls();
      const first = focusable[0] || dialog;
      const last = focusable.at(-1) || dialog;
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    layer.querySelectorAll(closeSelector).forEach((control) => control.addEventListener("click", close));
    document.addEventListener("keydown", onKeydown);
    window.setTimeout(() => {
      if (!closed) (dialog.contains(preferredFocus) && canReceiveFocus(preferredFocus) ? preferredFocus : controls()[0] || dialog).focus();
    }, 20);
    return close;
  }

  function setupNavigation() {
    document.querySelectorAll('.nav-desktop .nav-item--has-menu').forEach((item) => {
      const link = item.querySelector(':scope > a');
      if (!link) return;
      const open = () => {
        item.classList.remove('is-dismissed');
        setExpanded(link, true);
      };
      setExpanded(link, false);
      item.addEventListener('pointerenter', open);
      item.addEventListener('focusin', open);
      item.addEventListener('pointerleave', () => {
        if (!item.contains(document.activeElement)) setExpanded(link, false);
      });
      item.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!item.contains(document.activeElement)) {
            item.classList.remove('is-dismissed');
            setExpanded(link, false);
          }
        });
      });
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        link.focus();
        item.classList.add('is-dismissed');
        setExpanded(link, false);
      });
    });
  }

  function setupDrawers() {
    const mobileTrigger = document.querySelector("[data-mobile-nav-open]");
    const mobileLayer = document.querySelector("[data-mobile-nav]");
    let closeMobile;
    mobileTrigger?.addEventListener("click", () => {
      closeMobile = openLayer(mobileLayer, mobileTrigger, "[data-mobile-nav-close]");
    });
    window.addEventListener("resize", () => {
      if (window.innerWidth > 1099 && mobileLayer?.dataset.open === "true") closeMobile?.();
    });

    const searchTrigger = document.querySelector("[data-search-open]");
    const searchLayer = document.querySelector("[data-search-drawer]");
    const searchInput = searchLayer?.querySelector("[data-predictive-input]");
    searchTrigger?.addEventListener("click", () =>
      openLayer(
        searchLayer,
        searchTrigger,
        "[data-search-close]",
        searchInput,
      ),
    );

    const facetsTrigger = document.querySelector("[data-facets-open]");
    const facetsLayer = document.querySelector("[data-facets-drawer]");
    let closeFacets;
    facetsTrigger?.addEventListener("click", () => {
      closeFacets = openLayer(
        facetsLayer,
        facetsTrigger,
        "[data-facets-close]",
        facetsLayer?.querySelector("[data-facets-close]"),
      );
    });
    window.addEventListener("resize", () => {
      if (window.innerWidth > 999 && facetsLayer) {
        if (facetsLayer.dataset.open === "true") closeFacets?.();
        facetsLayer.hidden = false;
        delete facetsLayer.dataset.open;
        setExpanded(facetsTrigger, false);
      }
    });
  }

  function setupPredictiveSearch() {
    const form = document.querySelector("[data-predictive-search]");
    const input = form?.querySelector("[data-predictive-input]");
    const results = form
      ?.closest("[data-search-drawer]")
      ?.querySelector("[data-predictive-results]");
    if (!form || !input || !results) return;

    const discovery = form.closest("[data-search-drawer]")?.querySelector("[data-search-discovery]");
    const copy = (key, fallback) => storeCopy[key] || fallback;
    const showHint = (message) => {
      results.innerHTML = message ? `<p class="predictive-results__hint">${escapeHTML(message)}</p>` : "";
    };
    const syncDiscovery = () => {
      if (discovery) discovery.hidden = input.value.trim().length >= 2;
    };
    let controller;
    let requestVersion = 0;
    const search = debounce(async (version) => {
      if (version !== requestVersion) return;
      const query = input.value.trim();
      if (query.length < 2) {
        results.setAttribute("aria-busy", "false");
        showHint(query ? copy("search_minimum", "Enter at least two characters.") : "");
        return;
      }

      controller?.abort();
      controller = new AbortController();
      results.setAttribute("aria-busy", "true");
      showHint(copy("search_loading", "Searching…"));
      try {
        const base = routes.searchSuggest || "/search/suggest";
        const params = new URLSearchParams({
          q: query,
          "resources[type]": "product,collection,page,article",
          "resources[limit]": "8",
          "resources[options][unavailable_products]": "hide",
          section_id: "predictive-search",
        });
        const response = await fetch(`${base}?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error("Search request failed");
        const html = await response.text();
        if (version !== requestVersion) return;
        const documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        const section = documentFragment.querySelector(
          "#shopify-section-predictive-search",
        );
        if (!section) throw new Error("Predictive search section unavailable");
        results.innerHTML = section.innerHTML;
      } catch (error) {
        if (error.name === "AbortError" || version !== requestVersion) return;
        showHint(copy("search_unavailable", "Suggestions are unavailable. Press Search to see full results."));
      } finally {
        if (version === requestVersion) results.setAttribute("aria-busy", "false");
      }
    }, 180);

    input.addEventListener("input", () => {
      controller?.abort();
      requestVersion += 1;
      syncDiscovery();
      const query = input.value.trim();
      results.setAttribute("aria-busy", String(query.length >= 2));
      showHint(query.length >= 2 ? copy("search_loading", "Searching…") : query ? copy("search_minimum", "Enter at least two characters.") : "");
      search(requestVersion);
    });
    syncDiscovery();
    if (input.value.trim().length >= 2) search(requestVersion);
    form.addEventListener("submit", () => {
      const query = input.value.trim();
      track("search_performed", {
        query_length: Array.from(query).length,
        result_count: results.querySelectorAll('[data-search-suggestion]').length,
      });
    });
  }

  async function getCart() {
    const base = routes.cart || "/cart";
    const response = await fetch(`${base}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Unable to load cart");
    return response.json();
  }

  function updateCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = String(count);
      element.setAttribute("aria-label", `${count} ${count === 1 ? "item" : "items"}`);
    });
    document.querySelectorAll("[data-cart-count-label]").forEach((element) => {
      element.textContent = count === 1 ? element.dataset.singular : element.dataset.plural;
    });
  }

  function variantSwatchFill(label) {
    const value = String(label || "").toLowerCase();
    if (value.includes("mixed") || value.includes("multi")) {
      return "conic-gradient(#2f6fb3, #d85d69, #e8c84b, #4c9162, #2f6fb3)";
    }
    const colors = [
      ["peacock blue", "#087a82"],
      ["dark blue", "#173b67"],
      ["light blue", "#78b6d4"],
      ["rose red", "#c64061"],
      ["dark gray", "#4f5559"],
      ["dark grey", "#4f5559"],
      ["light gray", "#b9bcbd"],
      ["light grey", "#b9bcbd"],
      ["light green", "#9dca68"],
      ["dark green", "#285c49"],
      ["navy", "#1d2f4f"],
      ["cyan", "#39bad5"],
      ["blue", "#2f70b7"],
      ["gray", "#858b8f"],
      ["grey", "#858b8f"],
      ["red", "#cf4148"],
      ["pink", "#e78ba8"],
      ["purple", "#7555a6"],
      ["orange", "#e77938"],
      ["yellow", "#e8d34d"],
      ["lime", "#b7d93d"],
      ["green", "#4e9363"],
      ["beige", "#d8c6a7"],
      ["khaki", "#b49d72"],
      ["brown", "#765447"],
      ["white", "#f8f7f2"],
      ["silver", "linear-gradient(135deg, #f4f6f6, #92999d)"],
      ["gold", "linear-gradient(135deg, #f0d27a, #ad7c28)"],
      ["black", "#191b1c"],
    ];
    return colors.find(([name]) => value.includes(name))?.[1] || null;
  }

  function combinedVariantParts(optionName, label) {
    const option = String(optionName || "").toLowerCase();
    const value = String(label || "").trim();
    const hasColor = option.includes("color") || option.includes("colour");
    let match;

    if (option.includes("width") && (hasColor || option.includes("finish"))) {
      match = value.match(
        /^(\d+(?:\.\d+)?)\s*mm\s*(?:-\s*)?(.+?)(?:\s*\((fits[^)]+)\))?$/i,
      );
      if (match) {
        return {
          group: `${match[1]} mm`,
          groupMeta: match[3] || "",
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
          sort: Number(match[1]),
        };
      }
    }

    if (option.includes("pack") && hasColor) {
      match = value.match(/^(\d+)\s*pack\s*-\s*(.+)$/i);
      if (match) {
        return {
          group: `${match[1]} Pack`,
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
          sort: Number(match[1]),
        };
      }
    }

    if (option.includes("thickness") && (hasColor || option.includes("design"))) {
      match = value.match(/^(.+?)\s*-\s*(\d+(?:\.\d+)?)\s*mm$/i);
      if (match) {
        return {
          group: `${match[2]} mm`,
          label: match[1].trim(),
          fill: hasColor ? variantSwatchFill(match[1]) : null,
          sort: Number(match[2]),
        };
      }
    }

    if (option.includes("style") && hasColor) {
      match = value.match(/^(.+?)\s*-\s*(.+)$/i);
      if (match) {
        return {
          group: match[1].trim(),
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
        };
      }
    }

    if (option.includes("set") && hasColor) {
      match = value.match(/^(.+?)(?:\s*\(([^)]*set)\))?$/i);
      if (match) {
        return {
          group: match[2] || "Paddle only",
          label: match[1].trim(),
          fill: variantSwatchFill(match[1]),
          sort: match[2] ? 1 : 0,
        };
      }
    }

    if (option.includes("type") && option.includes("pack")) {
      match = value.match(/^((?:indoor|outdoor)\s+\d+-hole),\s*(\d+)\s*pack$/i);
      if (match) {
        return {
          group: match[1],
          label: `${match[2]} Pack`,
          fill: null,
          sort: Number(match[2]),
        };
      }
    }

    return null;
  }

  function enhanceCombinedVariantPicker(group) {
    if (!group || group.dataset.grouped === "true") return;
    const optionName = group.dataset.optionName || "";
    const buttons = [...group.querySelectorAll("[data-variant-pill]")];
    if (buttons.length < 3) return;

    const parsed = buttons.map((button, index) => {
      const original = button.dataset.variantLabel || button.textContent.trim();
      const parts = combinedVariantParts(optionName, original);
      return parts ? { button, index, original, ...parts } : null;
    });
    if (parsed.some((item) => !item)) return;

    const grouped = new Map();
    parsed.forEach((item) => {
      if (!grouped.has(item.group)) {
        grouped.set(item.group, {
          label: item.group,
          meta: item.groupMeta || "",
          sort: item.sort,
          firstIndex: item.index,
          items: [],
        });
      }
      const entry = grouped.get(item.group);
      if (!entry.meta && item.groupMeta) entry.meta = item.groupMeta;
      entry.items.push(item);
    });
    if (grouped.size < 2) return;

    const groups = [...grouped.values()].sort((a, b) => {
      if (Number.isFinite(a.sort) && Number.isFinite(b.sort)) {
        return a.sort - b.sort;
      }
      return a.firstIndex - b.firstIndex;
    });
    groups.forEach((entry) => {
      entry.items.sort((a, b) => {
        if (Number.isFinite(a.sort) && Number.isFinite(b.sort)) {
          return a.sort - b.sort;
        }
        return a.index - b.index;
      });
    });
    const fragment = document.createDocumentFragment();

    groups.forEach((entry, groupIndex) => {
      const section = document.createElement("div");
      section.className = "variant-option-group";
      const heading = document.createElement("div");
      heading.className = "variant-option-group__heading";
      heading.id = `VariantGroup-${group.dataset.target}-${groupIndex}`;

      const title = document.createElement("strong");
      title.textContent = entry.label;
      heading.appendChild(title);
      if (entry.meta) {
        const meta = document.createElement("span");
        meta.textContent = entry.meta.replace(/^fits\b/i, "Fits");
        heading.appendChild(meta);
      }

      const choices = document.createElement("div");
      choices.className = "variant-option-group__choices";
      entry.items.forEach((item) => {
        const button = item.button;
        button.classList.add("variant-pill--grouped");
        button.setAttribute("aria-label", item.original);
        button.setAttribute("aria-describedby", heading.id);
        button.title = item.original;
        button.textContent = "";
        if (item.fill) {
          const swatch = document.createElement("span");
          swatch.className = "variant-pill__swatch";
          swatch.style.setProperty("--swatch-fill", item.fill);
          swatch.setAttribute("aria-hidden", "true");
          button.appendChild(swatch);
        }
        const text = document.createElement("span");
        text.textContent = item.label;
        button.appendChild(text);
        choices.appendChild(button);
      });

      section.append(heading, choices);
      fragment.appendChild(section);
    });

    group.textContent = "";
    group.appendChild(fragment);
    group.classList.add("variant-pills--grouped");
    group.dataset.grouped = "true";
  }

  async function addToCart(form) {
    if (form.dataset.adding === "true") return;
    const message = form.querySelector("[data-product-form-message]");
    const button = form.querySelector("[data-add-to-cart],button[type='submit']");
    const previousLabel = button?.innerHTML;
    const buttonWasDisabled = button?.disabled || false;
    const productRoot = form.closest("[data-product-root]");
    const merchantBlocked =
      productRoot?.dataset.merchantBlocked === "true";
    if (merchantBlocked) {
      const text =
        storeCopy.unavailable || "This item is currently unavailable. Please contact us for help.";
      announce(text);
      if (message) message.textContent = text;
      return;
    }
    const submittedData = new FormData(form);
    const submittedQuantity = Number(submittedData.get("quantity") || 1);
    form.dataset.adding = "true";
    if (button) {
      button.disabled = true;
      button.textContent = "Adding…";
    }
    if (productRoot) document.querySelectorAll("[data-sticky-submit]").forEach((sticky) => {
      sticky.disabled = true;
      sticky.textContent = "Adding…";
    });
    if (message) message.textContent = "";

    try {
      const response = await fetch(routes.cartAdd || "/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: submittedData,
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || "This item could not be added.");
      }
      // The add is already confirmed. A failed badge refresh must not invite
      // the shopper to repeat a successful add and double their quantity.
      try {
        const cart = await getCart();
        updateCartCount(cart.item_count);
      } catch (_error) { /* The cart page will show the authoritative quantity. */ }
      announce(`${payload.product_title || "Product"} added to cart.`);
      if (message) {
        message.innerHTML = `Added to cart. <a href="${
          routes.cart || "/cart"
        }">View cart</a>`;
      }
      track("product_added_to_cart", {
        product_id: String(payload.product_id || ""),
        variant_id: String(payload.variant_id || ""),
        quantity: submittedQuantity,
        value: Number(payload.final_price || payload.price || 0) / 100,
      });
    } catch (error) {
      const text = error.message || "This item could not be added.";
      announce(text);
      if (message) message.textContent = text;
    } finally {
      delete form.dataset.adding;
      const option = form.querySelector("[data-variant-select]")?.selectedOptions[0];
      if (button) {
        button.innerHTML = previousLabel;
        button.disabled = option ? merchantBlocked || option.dataset.available !== "true" : buttonWasDisabled;
        const label = button.querySelector("[data-add-to-cart-label]");
        if (label && option) label.textContent = merchantBlocked ? "Currently unavailable" : option.dataset.available === "true" ? "Add to cart" : "Unavailable";
      }
      if (option) document.querySelectorAll("[data-sticky-submit]").forEach((sticky) => {
        sticky.disabled = merchantBlocked || option.dataset.available !== "true";
        sticky.textContent = merchantBlocked ? "Currently unavailable" : option.dataset.available === "true" ? "Add to cart" : "Unavailable";
      });
    }
  }

  function setupProductForms() {
    document
      .querySelectorAll(".variant-pills[data-variant-pills]")
      .forEach((group) => {
        enhanceCombinedVariantPicker(group);
        if (group.dataset.grouped === "true" || !/^pack\s*size$/i.test(group.dataset.optionName?.trim() || "")) return;
        const packs = [...group.querySelectorAll("[data-variant-pill]")].map((button) => {
          const label = button.dataset.variantLabel || button.textContent.trim();
          const match = label.match(/^(\d+)\s*(?:balls?|packs?|pieces?)$/i);
          return match ? { button, count: Number(match[1]) } : null;
        });
        // Reorder only simple numeric pack labels. Selection, variant IDs and
        // grouped or descriptive options keep their original meaning.
        if (packs.length > 1 && packs.every(Boolean)) {
          packs.sort((a, b) => a.count - b.count).forEach(({ button }) => group.appendChild(button));
        }
      });

    document
      .querySelectorAll("[data-product-form],[data-product-card-form]")
      .forEach((form) => {
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          addToCart(form);
        });
      });

    document.querySelectorAll("[data-variant-select]").forEach((select) => {
      select.addEventListener("change", () => {
        const option = select.selectedOptions[0];
        const root = select.closest("[data-product-root]");
        const merchantBlocked = root?.dataset.merchantBlocked === "true";
        const variantAvailable = option?.dataset.available === "true";
        const available = variantAvailable && !merchantBlocked;
        const price = option?.dataset.priceFormatted || "";
        const comparePrice = Number(option?.dataset.comparePrice || 0);
        const currentPrice = Number(option?.dataset.price || 0);

        root?.querySelectorAll("[data-price-wrap] [data-product-price]").forEach((node) => {
          node.textContent = price;
        });
        root?.querySelectorAll("[data-compare-toggle]").forEach((button) => {
          button.dataset.productPrice = price;
          if (option?.dataset.url) button.dataset.productUrl = option.dataset.url;
        });
        const compare = root?.querySelector("[data-product-compare-price]");
        if (compare) {
          compare.textContent = option?.dataset.compareFormatted || "";
          compare.hidden = !(comparePrice > currentPrice);
        }
        root?.querySelectorAll("[data-add-to-cart]").forEach((button) => {
          button.disabled = !available || button.closest("form")?.dataset.adding === "true";
          const label = button.querySelector("[data-add-to-cart-label]");
          if (label) {
            label.textContent = merchantBlocked
              ? "Currently unavailable"
              : available
                ? "Add to cart"
                : "Unavailable";
          }
        });
        document
          .querySelectorAll("[data-sticky-price]")
          .forEach((node) => (node.textContent = price));
        document
          .querySelectorAll("[data-sticky-submit]")
          .forEach((button) => {
            const adding = root?.querySelector("[data-product-form]")?.dataset.adding === "true";
            button.disabled = !available || adding;
            button.textContent = adding ? "Adding…" : merchantBlocked
              ? "Currently unavailable"
              : available
                ? "Add to cart"
                : "Unavailable";
          });
        const availability = root?.querySelector("[data-variant-availability]");
        if (availability) {
          availability.classList.toggle(
            "inventory-status--unavailable",
            !available,
          );
          availability.querySelector("strong").textContent = merchantBlocked
            ? "Not offered for sale"
            : available
              ? "Available"
              : "Currently unavailable";
        }
        const availabilityNote = availability?.querySelector("span");
        if (availabilityNote && !merchantBlocked) availabilityNote.hidden = available;
        if (option?.dataset.url) {
          window.history.replaceState({}, "", option.dataset.url);
        }

        // Swap the main gallery image to this variant's own media. Variants
        // without an assigned image leave the gallery where it is - jumping to
        // an unrelated slide would be worse than not moving.
        const variantMediaId = option?.dataset.mediaId;
        if (variantMediaId && selectProductMedia) {
          selectProductMedia(variantMediaId, false);
        }

        // Refresh an existing shortlist entry when its selected variant changes.
        const compareButton = root?.querySelector("[data-compare-toggle]");
        if (compareButton) {
          const items = readComparisonItems();
          const index = items.findIndex((item) => String(item.id) === String(compareButton.dataset.productId));
          if (index >= 0) {
            items[index] = compareSnapshot(compareButton);
            writeStorage(COMPARE_KEY, items);
            syncCompareControls(items);
          }
        }

        // Keep the pill row in step when the select changes for any reason.
        const pills = root?.querySelector("[data-variant-pills]");
        if (pills) {
          let selectedPill = null;
          pills.querySelectorAll("[data-variant-pill]").forEach((pill) => {
            const isSelected = pill.dataset.variantPill === select.value;
            pill.classList.toggle("is-selected", isSelected);
            pill.setAttribute("aria-pressed", String(isSelected));
            if (isSelected) selectedPill = pill;
          });
          const choice = root?.querySelector("[data-variant-choice]");
          if (choice && selectedPill) {
            choice.textContent = selectedPill.dataset.variantLabel || "";
          }
        }
      });
    });

    // Pills drive the select rather than replacing it, so the form value and
    // every existing variant behaviour stay in one place.
    document.querySelectorAll("[data-variant-pills]").forEach((group) => {
      const select = document.getElementById(group.dataset.target);
      if (!select) return;

      group.addEventListener("click", (event) => {
        const pill = event.target.closest("[data-variant-pill]");
        if (!pill || pill.dataset.variantPill === select.value) return;
        select.value = pill.dataset.variantPill;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  /*
    Set by setupProductGallery so the variant handler can drive the gallery.
    Choosing a colour used to update the price, the availability and the URL but
    leave the photo on whatever was showing, which read as the wrong product.
  */
  let selectProductMedia = null;

  function setupProductGallery() {
    const gallery = document.querySelector("[data-product-gallery]");
    const thumbs = document.querySelectorAll("[data-media-thumb]");
    if (!gallery || !thumbs.length) return;

    const mediaItems = [...gallery.querySelectorAll("[data-media-id]")];
    const selectMedia = (mediaId) => {
      const selected = mediaItems.find(
        (item) => String(item.dataset.mediaId) === String(mediaId),
      );
      if (!selected) return;

      mediaItems.forEach((item) => {
        const active = item === selected;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-hidden", active ? "false" : "true");
      });
      thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaThumb) === String(mediaId);
        thumb.classList.toggle("is-active", active);
        if (active) thumb.setAttribute("aria-current", "true");
        else thumb.removeAttribute("aria-current");
      });

    };

    // Seed aria-hidden from what the template rendered active, not from index
    // 0 - with a ?variant= link those are not the same slide.
    mediaItems.forEach((item) => {
      item.setAttribute(
        "aria-hidden",
        item.classList.contains("is-active") ? "false" : "true",
      );
    });
    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", (event) => {
        event.preventDefault();
        selectMedia(thumb.dataset.mediaThumb);
      });
    });

    selectProductMedia = selectMedia;
  }

  function setupQuantityControls() {
    document.addEventListener("click", (event) => {
      const minus = event.target.closest("[data-quantity-minus]");
      const plus = event.target.closest("[data-quantity-plus]");
      if (!minus && !plus) return;
      const control = event.target.closest("[data-quantity-control]");
      const input = control?.querySelector("[data-quantity-input]");
      if (!input) return;
      const minimum = Number(input.min || 0);
      const maximum = Number(input.max || 99);
      const current = Number(input.value || 0);
      input.value = String(
        Math.min(maximum, Math.max(minimum, current + (plus ? 1 : -1))),
      );
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  async function changeCartLine(lineElement, quantity) {
    const key = lineElement.dataset.key;
    const identifier = key ? { id: key } : { line: Number(lineElement.dataset.line) };
    const response = await fetch(routes.cartChange || "/cart/change.js", {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ ...identifier, quantity }),
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || "Cart could not be updated.");
    return cart;
  }

  function updateCartUI(cart, changedLine, requestedQuantity, pending) {
    updateCartCount(cart.item_count);
    document.querySelectorAll("[data-cart-total]").forEach((node) => (node.textContent = money(cart.total_price)));
    if (requestedQuantity === 0) changedLine.remove();
    const lines = [...document.querySelectorAll("[data-cart-line]")];
    if (cart.item_count === 0 || lines.length !== cart.items.length) {
      window.location.reload();
      return;
    }
    // Serialized changes preserve line order. Refresh every key because Shopify
    // can change keys when discounts change, as well as when an item is removed.
    lines.forEach((line, index) => {
      const item = cart.items[index];
      line.dataset.line = String(index + 1);
      if (item.key) line.dataset.key = item.key;
      const input = line.querySelector("[data-quantity-input]");
      input.dataset.cartQuantity = String(item.quantity);
      if (!pending.has(line)) input.value = String(item.quantity);
      const price = line.querySelector(".cart-line__price strong");
      if (price) price.textContent = money(item.final_line_price);
      let original = line.querySelector(".cart-line__price s");
      if (item.original_line_price > item.final_line_price && price) {
        if (!original) { original = document.createElement("s"); price.before(original); }
        original.textContent = money(item.original_line_price);
      } else original?.remove();
      const remove = line.querySelector("[data-cart-remove]");
      if (remove) {
        const url = new URL(`${routes.cart || "/cart"}/change`, window.location.origin);
        url.searchParams.set(item.key ? "id" : "line", item.key || String(index + 1));
        url.searchParams.set("quantity", "0");
        remove.href = url.toString();
      }
    });
    const progress = document.querySelector("[data-shipping-progress]");
    if (progress) {
      const threshold = Number(progress.dataset.threshold || 0);
      const percent = threshold ? Math.min(100, (cart.total_price / threshold) * 100) : 100;
      const bar = progress.querySelector(".free-shipping-progress__track i");
      if (bar) bar.style.width = `${percent}%`;
      const heading = progress.querySelector("strong");
      if (heading) heading.textContent = cart.total_price >= threshold
        ? (storeCopy.shipping_reached || "Your bag qualifies for free shipping.")
        : (storeCopy.shipping_remaining || "Add __amount__ for free shipping.").replace("__amount__", money(threshold - cart.total_price));
    }
  }

  function setupCart() {
    const cartRoot = document.querySelector("[data-cart-root]");
    if (!cartRoot) return;
    const form = cartRoot.querySelector("[data-cart-form]");
    const message = cartRoot.querySelector("[data-cart-message]");
    const submitButtons = [...cartRoot.querySelectorAll('button[type="submit"]')];
    const originallyDisabled = new Map(submitButtons.map((button) => [button, button.disabled]));
    const merchantBlocked = cartRoot.querySelector("[data-checkout-button]")?.disabled === true;
    const pending = new Map();
    let timer;
    let running = false;
    let busy = false;
    cartRoot.querySelectorAll("[data-quantity-input]").forEach((input) => { input.dataset.cartQuantity = input.value; });
    const say = (text) => { announce(text); if (message) message.textContent = text; };
    const setBusy = (value) => {
      busy = value;
      cartRoot.setAttribute("aria-busy", String(value));
      // Quantity fields stay enabled so native updates[] serialization remains complete.
      submitButtons.forEach((button) => { button.disabled = value || originallyDisabled.get(button); });
    };
    const removing = (line, value) => {
      const input = line.querySelector("[data-quantity-input]");
      if (input) input.readOnly = value;
      line.querySelectorAll("[data-quantity-minus], [data-quantity-plus]").forEach((button) => { button.disabled = value; });
      const remove = line.querySelector("[data-cart-remove]");
      if (remove) remove.setAttribute("aria-disabled", String(value));
    };
    const flush = async () => {
      if (running) return;
      running = true;
      while (pending.size) {
        const [line, quantity] = pending.entries().next().value;
        pending.delete(line);
        if (!line.isConnected) continue;
        try {
          const cart = await changeCartLine(line, quantity);
          updateCartUI(cart, line, quantity, pending);
          if (merchantBlocked) { window.location.reload(); return; }
          say(quantity === 0 ? "Item removed." : "Cart updated.");
        } catch (error) {
          const input = line.querySelector("[data-quantity-input]");
          if (input && !pending.has(line)) input.value = input.dataset.cartQuantity;
          removing(line, false);
          say(error.message || "Cart could not be updated.");
        }
      }
      running = false;
      setBusy(false);
    };
    const queue = (line, quantity) => {
      if (!line) return;
      pending.set(line, quantity);
      if (quantity === 0) removing(line, true);
      setBusy(true);
      say("Updating cart…");
      window.clearTimeout(timer);
      if (!running) timer = window.setTimeout(flush, 220);
    };
    form?.addEventListener("submit", (event) => {
      if (busy) { event.preventDefault(); say("Please wait while your cart updates."); }
    });
    cartRoot.addEventListener("change", (event) => {
      const input = event.target;
      if (!input.matches("[data-quantity-input]") || input.readOnly) return;
      const quantity = Number(input.value);
      if (!input.value.trim() || !Number.isInteger(quantity) || !input.checkValidity()) {
        input.reportValidity();
        input.value = input.dataset.cartQuantity;
        say("Enter a valid whole-number quantity.");
        return;
      }
      queue(input.closest("[data-cart-line]"), quantity);
    });
    cartRoot.addEventListener("click", (event) => {
      const remove = event.target.closest("[data-cart-remove]");
      if (!remove) return;
      event.preventDefault();
      if (remove.getAttribute("aria-disabled") === "true") return;
      queue(remove.closest("[data-cart-line]"), 0);
    });
  }

  function readStorage(key, fallback = []) {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || "");
      return Array.isArray(value) ? value : fallback;
    } catch (_error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function readComparisonItems() {
    const seen = new Set();
    return readStorage(COMPARE_KEY).map((item) => {
      if (!item || typeof item !== "object" || !item.id || !item.url) return null;
      try {
        const url = new URL(item.url, window.location.origin);
        const match = url.pathname.match(/\/products\/([a-z0-9][a-z0-9-]*)\/?$/i);
        if (url.origin !== window.location.origin || !match) return null;
        const variant = url.searchParams.get("variant");
        if (variant && !/^\d+$/.test(variant)) return null;
        return { ...item, id: String(item.id), handle: match[1], url: `${url.pathname}${variant ? `?variant=${variant}` : ""}` };
      } catch (_error) {
        return null;
      }
    }).filter((item) => {
      if (!item || typeof item !== "object" || !item.id || seen.has(String(item.id))) return false;
      seen.add(String(item.id));
      return true;
    }).slice(0, MAX_COMPARE);
  }

  function compareSnapshot(button) {
    const data = button.dataset;
    const root = button.closest("[data-product-card], [data-product-root]");
    const image = root?.querySelector(".product-media.is-active img, .product-card__image");
    const variant = root?.querySelector("[data-variant-select]")?.selectedOptions[0];
    return {
      id: data.productId,
      image: image?.currentSrc || image?.src || "",
      handle: data.productHandle,
      url: variant?.dataset.url || data.productUrl,
      title: data.productTitle,
      vendor: data.productVendor,
      price: variant?.dataset.priceFormatted || data.productPrice,
      variant: variant?.textContent.trim().split(" — ")[0] || data.productVariant || "",
      availability: (variant?.dataset.available || data.productAvailable) === "true" ? "Available" : (variant?.dataset.available || data.productAvailable) === "false" ? "Unavailable" : "",
      shape: data.productShape,
      dimensions: data.productDimensions,
      weight: data.productWeight,
      swing: data.productSwing,
      twist: data.productTwist,
      handleLength: data.productHandleLength,
      grip: data.productGrip,
      core: data.productCore,
      face: data.productFace,
      construction: data.productConstruction,
      power: data.productPower,
      control: data.productControl,
      spin: data.productSpin,
      sweetSpot: data.productSweetSpot,
      maneuver: data.productManeuver,
      skill: data.productSkill,
      style: data.productStyle,
      warranty: data.productWarranty,
      approval: data.productApproval,
    };
  }

  function syncCompareControls(items = readComparisonItems()) {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll("[data-compare-toggle]").forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("is-active", active);
      button.textContent = active ? "Compared ✓" : "Compare";
    });
    document
      .querySelectorAll("[data-compare-count]")
      .forEach((node) => (node.textContent = String(items.length)));
    document.querySelectorAll("[data-compare-shortlist]").forEach((bar) => {
      bar.hidden = items.length === 0;
    });
  }

  function renderComparison(items = readComparisonItems()) {
    const mount = document.querySelector("[data-compare-mount]");
    if (!mount) return;
    const empty = mount.querySelector("[data-compare-empty]");
    const actions = mount.querySelector("[data-compare-actions]");
    const wrap = mount.querySelector("[data-compare-table]");
    const focused = wrap.contains(document.activeElement) ? document.activeElement : null;
    const focusedId = focused?.dataset.compareRemove;
    const focusedUrl = focused?.getAttribute("href");
    const focusedIndex = focusedId ? [...wrap.querySelectorAll("[data-compare-remove]")].indexOf(focused) : 0;

    if (!items.length) {
      empty.hidden = false;
      actions.hidden = true;
      wrap.hidden = true;
      wrap.innerHTML = "";
      syncCompareControls(items);
      return;
    }

    const fields = [
      ["Price", "price"],
      ["Selected option", "variant"],
      ["Availability", "availability"],
      ["Brand", "vendor"],
      ["Shape", "shape"],
      ["Dimensions", "dimensions"],
      [storeCopy.listed_weight || "Listed weight", "weight"],
      ["Swing weight", "swing"],
      ["Twist weight", "twist"],
      ["Handle length", "handleLength"],
      ["Grip circumference", "grip"],
      ["Core thickness", "core"],
      ["Face material", "face"],
      ["Core construction", "construction"],
      ["Skill level", "skill"],
      ["Playing style", "style"],
      ["Warranty", "warranty"],
      ["Approval status", "approval"],
    ];

    const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";
    wrap.innerHTML = `
      <table class="compare-table" style="--compare-columns: ${items.length}">
        <thead>
          <tr>
            <th scope="col">Specification</th>
            ${items
              .map(
                (item) => `
                  <th scope="col">
                    ${item.image ? `<a class="pgc-compare-image" href="${escapeHTML(item.url || "#")}" tabindex="-1" aria-hidden="true"><img src="${escapeHTML(item.image)}" alt="" loading="lazy" width="180" height="180"></a>` : ""}
                    <span>${escapeHTML(item.vendor || "")}</span>
                    <a href="${escapeHTML(item.url || "#")}">${escapeHTML(
                      item.title || "Paddle",
                    )}</a>
                    <button type="button" class="text-control" data-compare-remove="${escapeHTML(
                      item.id,
                    )}" aria-label="Remove ${escapeHTML(item.title || "paddle")} from comparison">Remove</button>
                  </th>`,
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${fields
            .filter(([, key]) => items.some((item) => hasValue(item[key])))
            .map(
              ([label, key]) => `
                <tr>
                  <th scope="row">${escapeHTML(label)}</th>
                  ${items
                    .map(
                      (item) =>
                        `<td>${escapeHTML(hasValue(item[key]) ? item[key] : "Not listed")}</td>`,
                    )
                    .join("")}
                </tr>`,
            )
            .join("")}
        </tbody>
      </table>`;
    empty.hidden = true;
    actions.hidden = false;
    wrap.hidden = false;
    syncCompareControls(items);
    if (focused) {
      const controls = [...wrap.querySelectorAll("[data-compare-remove]")];
      const next = focusedId
        ? controls.find((button) => button.dataset.compareRemove === focusedId) || controls[Math.min(focusedIndex, controls.length - 1)]
        : [...wrap.querySelectorAll("a")].find((link) => link.getAttribute("href") === focusedUrl);
      (next || wrap).focus({ preventScroll: true });
    }
  }

  function setupCompare() {
    const mount = document.querySelector("[data-compare-mount]");
    let lastCompareTrigger = null;
    const status = mount?.querySelector("[data-compare-status]");
    const retry = mount?.querySelector("[data-compare-retry]");
    const wrap = mount?.querySelector("[data-compare-table]");
    const hint = mount?.querySelector("[data-compare-scroll-hint]");
    const updateScrollHint = () => {
      if (hint && wrap) hint.hidden = wrap.hidden || wrap.scrollWidth <= wrap.clientWidth + 1;
    };
    const resizeObserver = mount && typeof ResizeObserver === "function" ? new ResizeObserver(updateScrollHint) : null;
    let observedTable = null;
    if (resizeObserver) resizeObserver.observe(wrap);
    else if (mount) window.addEventListener("resize", updateScrollHint);
    let copy = {};
    try { copy = JSON.parse(mount?.querySelector("[data-compare-copy]")?.textContent || "{}"); } catch (_error) { /* Use readable fallbacks. */ }
    let requestVersion = 0;
    const verified = new Map();
    const keyFor = (item) => `${item.id}:${item.url}`;
    const update = (message = "", busy = false, canRetry = false) => {
      const items = readComparisonItems();
      syncCompareControls(items);
      if (!mount) return;
      const visible = items.map((item) => verified.get(keyFor(item))).filter(Boolean);
      renderComparison(visible);
      syncCompareControls(items);
      mount.setAttribute("aria-busy", String(busy));
      mount.querySelector("[data-compare-empty]").hidden = items.length > 0 || busy;
      mount.querySelector("[data-compare-actions]").hidden = items.length === 0;
      if (resizeObserver) {
        if (observedTable) resizeObserver.unobserve(observedTable);
        observedTable = wrap.querySelector("table");
        if (observedTable) resizeObserver.observe(observedTable);
      }
      updateScrollHint();
      if (status) { status.textContent = message; status.hidden = !message; }
      if (retry) retry.hidden = !canRetry;
    };
    const refresh = async () => {
      const version = ++requestVersion;
      const items = readComparisonItems();
      if (!mount || !items.length) { update(); return; }
      const needsRefresh = items.some((item) => !verified.has(keyFor(item)));
      update(needsRefresh ? (copy.loading || "Checking current product details…") : "", needsRefresh);
      const checked = await Promise.all(items.map(async (item) => {
        if (verified.has(keyFor(item))) return { original: item, item: verified.get(keyFor(item)) };
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(item.url, { cache: "no-store", headers: { Accept: "text/html" }, signal: controller.signal });
          if (response.status === 404 || response.status === 410) return { original: item, remove: true };
          if (!response.ok) return { original: item };
          const page = new DOMParser().parseFromString(await response.text(), "text/html");
          const product = page.querySelector("[data-product-root]");
          if (!product || String(product.dataset.productId) !== item.id) return { original: item };
          const button = product.querySelector("[data-compare-toggle]");
          if (product.dataset.merchantBlocked === "true" || !button) return { original: item, remove: true };
          const selectedId = new URL(item.url, window.location.origin).searchParams.get("variant");
          const select = product.querySelector("[data-variant-select]");
          if (selectedId) {
            const option = [...(select?.options || [])].find((entry) => entry.value === selectedId);
            if (!option) return { original: item, remove: true };
            select.value = selectedId;
          }
          const snapshot = compareSnapshot(button);
          // The selected option is explicit, including a sold-out option. Never
          // replace it with a different variant merely to show an available price.
          if (snapshot.variant === "Default Title") snapshot.variant = "";
          return { original: item, item: snapshot };
        } catch (_error) {
          return { original: item };
        } finally {
          window.clearTimeout(timeout);
        }
      }));
      if (version !== requestVersion || !mount.isConnected) return;
      const results = new Map(checked.map((result) => [keyFor(result.original), result]));
      let removed = false;
      let failed = false;
      const latest = readComparisonItems().flatMap((item) => {
        const result = results.get(keyFor(item));
        if (result?.remove) { removed = true; return []; }
        if (result?.item) {
          verified.set(keyFor(result.item), result.item);
          return [result.item];
        }
        failed = true;
        return [item];
      });
      writeStorage(COMPARE_KEY, latest);
      update(failed ? (copy.retry_notice || "Some saved paddles could not be checked. Retry to see their current details.") : removed ? (copy.removed_notice || "Items that are no longer available have been removed from your comparison.") : "", false, failed);
    };
    retry?.addEventListener("click", () => { void refresh(); });
    window.addEventListener("storage", (event) => {
      if (event.key !== COMPARE_KEY && event.key !== null) return;
      verified.clear();
      void refresh();
    });
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-compare-toggle]");
      const remove = event.target.closest("[data-compare-remove]");
      const clear = event.target.closest("[data-compare-clear]");
      let items = readComparisonItems();

      if (toggle) {
        lastCompareTrigger = toggle;
        const snapshot = compareSnapshot(toggle);
        const existing = items.findIndex(
          (item) => String(item.id) === String(snapshot.id),
        );
        if (existing >= 0) {
          items.splice(existing, 1);
          announce(`${snapshot.title} removed from comparison.`);
        } else {
          if (items.length >= MAX_COMPARE) {
            announce("Comparison is limited to four paddles.");
            return;
          }
          items.push(snapshot);
          announce(`${snapshot.title} added to comparison.`);
          track("product_compared", {
            product_id: String(snapshot.id || ""),
            product_name: snapshot.title || "",
            compare_count: items.length,
          });
        }
        if (!writeStorage(COMPARE_KEY, items)) {
          announce("Comparison could not be saved. Please allow browser storage and try again.");
          return;
        }
        verified.set(keyFor(snapshot), snapshot);
        syncCompareControls(items);
        void refresh();
      } else if (remove) {
        items = items.filter(
          (item) => String(item.id) !== String(remove.dataset.compareRemove),
        );
        writeStorage(COMPARE_KEY, items);
        update();
        if (!items.length) mount?.querySelector("[data-compare-empty] a")?.focus({ preventScroll: true });
        void refresh();
      } else if (clear) {
        if (!writeStorage(COMPARE_KEY, [])) {
          announce("Comparison could not be saved. Please allow browser storage and try again.");
          return;
        }
        const shortlistBar = clear.closest("[data-compare-shortlist]");
        verified.clear();
        void refresh();
        if (shortlistBar) {
          const focusTarget = lastCompareTrigger?.isConnected ? lastCompareTrigger : document.querySelector("[data-compare-toggle]");
          focusTarget?.focus({ preventScroll: true });
          announce(shortlistBar.dataset.clearAnnouncement);
        } else {
          mount?.querySelector("[data-compare-empty] a")?.focus({ preventScroll: true });
        }
      }
    });
    void refresh();
  }

  function setupFinder() {
    const root = document.querySelector("[data-finder-root]");
    const form = root?.querySelector("[data-finder-form]");
    const source = root?.querySelector("[data-finder-products]");
    const results = root?.querySelector("[data-finder-results]");
    if (!root || !form || !source || !results) return;

    let products = [];
    let copy = {};
    let productsReady = true;
    try {
      products = JSON.parse(source.textContent || "[]");
      if (!Array.isArray(products)) throw new TypeError("Invalid finder catalog");
      copy = JSON.parse(root.querySelector("[data-finder-copy]")?.textContent || "{}");
    } catch (_error) {
      products = [];
      productsReady = false;
    }
    const reset = root.querySelector("[data-finder-reset]");
    const summaries = [...root.querySelectorAll("[data-finder-answer]")];
    const emptyLabels = new Map(summaries.map((node) => [node, node.textContent]));
    const questionCount = form.querySelectorAll("fieldset").length;
    const choiceLabel = (input) => input?.closest("label")?.querySelector("[data-finder-choice-label]")?.textContent.trim() || input?.value || "";
    const scrollTo = (element) => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      element.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    };
    const updateAnswers = () => {
      const selected = [...form.querySelectorAll('input[type="radio"]:checked')];
      const progress = root.querySelector("[data-finder-progress]");
      if (progress && questionCount) {
        progress.style.setProperty("--progress", `${Math.min(100, selected.length / questionCount * 100)}%`);
      }
      const progressLabel = root.querySelector("[data-finder-progress-label]");
      if (progressLabel) progressLabel.textContent = (copy.progress || "__count__ / 3 answered").replace("__count__", String(selected.length));
      summaries.forEach((node) => {
        const input = selected.find((choice) => choice.name === node.dataset.finderAnswer);
        node.textContent = input ? choiceLabel(input) : emptyLabels.get(node);
      });
    };
    const showResults = () => {
      results.hidden = false;
      if (reset) reset.hidden = false;
      results.focus({ preventScroll: true });
      scrollTo(results);
    };
    const editAnswers = () => {
      const input = form.querySelector('input[type="radio"]:checked') || form.querySelector('input[type="radio"]');
      input?.focus({ preventScroll: true });
      scrollTo(form);
    };
    let started = false;
    form.addEventListener("change", () => {
      if (!started) {
        track("quiz_started", { quiz: "paddle_finder" });
        started = true;
      }
      updateAnswers();
      results.hidden = true;
      results.innerHTML = "";
      if (reset) reset.hidden = false;
    });
    reset?.addEventListener("click", () => {
      form.reset();
      results.hidden = true;
      results.innerHTML = "";
      reset.hidden = true;
      started = false;
      updateAnswers();
      editAnswers();
    });
    results.addEventListener("click", (event) => {
      if (event.target.closest("[data-finder-edit]")) editAnswers();
    });
    updateAnswers();

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const answers = Object.fromEntries(new FormData(form).entries());
      if (!productsReady || typeof window.CourtFinderScoring?.rankProducts !== "function") {
        results.innerHTML = `<div class="empty-state"><p>${escapeHTML(copy.loading_error || "The finder is unavailable right now. Please browse our paddles.")}</p></div>`;
        const browseLink = root.querySelector(".pgc-finder-profile > a");
        if (browseLink) results.firstElementChild.append(browseLink.cloneNode(true));
        showResults();
        return;
      }
      const ranked = window.CourtFinderScoring.rankProducts(products, answers, 5, copy);
      const actions = `<div class="pgc-finder-results__actions"><button type="button" data-finder-edit>${escapeHTML(copy.edit_answers)}</button></div>`;
      const chips = [...form.querySelectorAll('input[type="radio"]:checked')].map((input) => `<span>${escapeHTML(choiceLabel(input))}</span>`).join("");
      if (!ranked.length) {
        results.innerHTML = `<div class="empty-state"><h2>${escapeHTML(copy.empty_title)}</h2><p>${escapeHTML(copy.empty_copy)}</p>${actions}</div>`;
        const browseLink = root.querySelector(".pgc-finder-profile > a");
        if (browseLink) results.querySelector(".pgc-finder-results__actions").append(browseLink.cloneNode(true));
      } else {
        results.innerHTML = `
          <div class="section-header"><div><span class="eyebrow">${escapeHTML(copy.results_eyebrow)}</span><h2>${escapeHTML(copy.results_title)}</h2></div>${actions}</div>
          <div class="pgc-finder-answer-chips">${chips}</div>
          <p>${escapeHTML(copy.results_note)}</p>
          <div class="finder-result-grid">
            ${ranked.map(({ product, reasons }, index) => `
              <article class="finder-result-card">
                ${product.image ? `<a class="finder-result-card__media" href="${escapeHTML(product.url)}" tabindex="-1" aria-hidden="true"><img class="finder-result-card__image" src="${escapeHTML(product.image)}"${product.imageSrcset ? ` srcset="${escapeHTML(product.imageSrcset)}"` : ""} sizes="(min-width: 1464px) 405px, (min-width: 900px) 28vw, (min-width: 600px) 42vw, 100vw" loading="lazy" decoding="async" alt="">${index === 0 ? `<span class="pgc-finder-best-match">${escapeHTML(copy.best_match)}</span>` : ""}</a>` : ""}
                ${product.vendor ? `<span class="eyebrow">${escapeHTML(product.vendor)}</span>` : ""}
                <h3><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3>
                <ul>${reasons.map((reason) => `<li>${escapeHTML(reason)}</li>`).join("")}</ul>
                <div class="finder-result-card__foot"><strong>${product.priceVaries ? `${escapeHTML(copy.price_from || "From")} ` : ""}${escapeHTML(product.priceFormatted)}</strong><a class="button button--ink" href="${escapeHTML(product.url)}">${escapeHTML(copy.view_paddle)}</a></div>
              </article>`).join("")}
          </div>`;
      }
      showResults();
      track("quiz_completed", { quiz: "paddle_finder", result_count: ranked.length, experience: String(answers.experience || ""), preference: String(answers.preference || ""), budget: String(answers.budget || "") });
    });
  }


  /*
    Guides are long enough now that a reader arriving from search needs to see
    the shape of the page first. Built from the headings rather than stored in
    each article body, so a future guide gets one without the author doing
    anything. Skipped under three headings, where it would be noise.
  */
  function setupGuideToc() {
    const body = document.querySelector("[data-guide-body]");
    if (!body) return;

    body.querySelectorAll("table").forEach((table) => {
      let wrapper = table.closest(".guide-table");
      if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.className = "guide-table";
        table.before(wrapper);
        wrapper.appendChild(table);
      }
      wrapper.tabIndex = 0;
      wrapper.setAttribute("role", "region");
      let previous = wrapper.previousElementSibling;
      while (previous && previous.tagName !== "H2") previous = previous.previousElementSibling;
      const context = table.caption?.textContent.trim() || previous?.textContent.trim();
      const tableLabel = body.dataset.tableLabel || "Comparison table";
      wrapper.setAttribute("aria-label", context ? `${tableLabel}: ${context}` : tableLabel);
    });

    if (body.querySelector(".guide-toc")) return;

    const headings = [...body.querySelectorAll("h2")].filter((heading) => heading.textContent.trim());
    if (headings.length < 3) return;

    const used = new Set();
    headings.forEach((heading, index) => {
      if (!heading.id) {
        const slug =
          heading.textContent
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 60) || `section-${index + 1}`;
        let id = slug;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${slug}-${n++}`;
        heading.id = id;
      }
      used.add(heading.id);
      heading.tabIndex = -1;
    });

    const nav = document.createElement("nav");
    nav.className = "guide-toc";
    const tocLabel = body.dataset.tocLabel || "On this page";
    nav.setAttribute("aria-label", tocLabel);
    const label = document.createElement("strong");
    label.textContent = tocLabel;
    const list = document.createElement("ol");
    headings.forEach((heading) => {
      const item = document.createElement("li");
      const link = document.createElement("a");
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    });
    nav.append(label, list);

    // After the opening paragraph, not above it: the lede is what tells the
    // reader whether the guide is for them at all.
    const lede = body.querySelector("p");
    if (lede && lede.parentElement === body) lede.after(nav);
    else body.prepend(nav);
  }

  async function setupRecentlyViewed() {
    const productRoot = document.querySelector("[data-product-root]");
    if (!productRoot) return;
    if (productRoot.dataset.merchantBlocked === "true") return;
    const id = String(productRoot.dataset.productId || "");
    const title =
      productRoot.querySelector(".product-info h1")?.textContent?.trim() || "";
    const vendor =
      productRoot.querySelector(".product-info .eyebrow")?.textContent?.trim() ||
      "";
    const price =
      productRoot.querySelector("[data-product-price]")?.textContent?.trim() ||
      "";
    const normalize = (item) => {
      if (!item || typeof item !== "object" || !item.id || !item.url) return null;
      try {
        const parsed = new URL(item.url, window.location.origin);
        if (parsed.origin !== window.location.origin) return null;
        const match = parsed.pathname.match(/\/products\/([^/]+)\/?$/);
        if (!match) return null;
        const handle = decodeURIComponent(match[1]);
        if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return null;
        return { ...item, id: String(item.id), handle, url: `/products/${encodeURIComponent(handle)}` };
      } catch (_error) {
        return null;
      }
    };
    const current = normalize({ id, title, vendor, price, url: window.location.pathname });
    if (!current) return;
    const seen = new Set([current.id]);
    const previous = readStorage(RECENT_KEY).map(normalize).filter((item) => {
      if (!item || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    }).slice(0, 7);
    writeStorage(RECENT_KEY, [current, ...previous]);

    const mount = document.querySelector("[data-recently-viewed]");
    const section = document.querySelector("[data-recently-viewed-section]");
    if (!mount || !section || !previous.length) return;
    section.hidden = true;
    mount.innerHTML = "";

    // Saved titles and prices can outlive a product's publication. Render only
    // products confirmed available by the current storefront response.
    const checked = await Promise.all(previous.map(async (item) => {
      try {
        const response = await fetch(`${item.url}.js`, { cache: "no-store", headers: { Accept: "application/json" } });
        if (response.status === 404 || response.status === 410) return { id: item.id, remove: true };
        if (!response.ok) return { id: item.id };
        const product = await response.json();
        if (String(product.id) !== item.id || product.available === false) return { id: item.id, remove: true };
        if (product.available !== true || !product.title || !Number.isFinite(Number(product.price))) return { id: item.id };
        return { id: item.id, item: { ...item, title: product.title, vendor: product.vendor || "", price: money(product.price) } };
      } catch (_error) {
        // A network failure should hide the unverified card without erasing
        // valid browsing history. It can be checked on the next product page.
        return { id: item.id };
      }
    }));
    const byId = new Map(checked.map((result) => [result.id, result]));
    const latest = readStorage(RECENT_KEY).map(normalize).filter(Boolean).flatMap((item) => {
      const result = byId.get(item.id);
      return result?.remove ? [] : [result?.item || item];
    }).slice(0, 8);
    writeStorage(RECENT_KEY, latest);
    const retained = new Set(latest.map((item) => item.id));
    const visible = checked.filter((result) => result.item && retained.has(result.id)).map((result) => result.item).slice(0, 4);
    if (!visible.length || !mount.isConnected) return;
    mount.innerHTML = visible
      .map(
        (item) => `
          <article class="content-card">
            <span class="eyebrow">${escapeHTML(item.vendor)}</span>
            <h3><a href="${escapeHTML(item.url)}">${escapeHTML(
              item.title,
            )}</a></h3>
            <strong>${escapeHTML(item.price)}</strong>
            <a href="${escapeHTML(item.url)}">View again →</a>
          </article>`,
      )
      .join("");
    section.hidden = false;
  }

  function setupFiltersAndSort() {
    const submittedPrices = new WeakMap();
    const submitFilter = (input) => {
      const form = input.closest("form");
      if (!form || !form.checkValidity()) return;
      if (input.type === "number") {
        const previousValue = submittedPrices.has(input)
          ? submittedPrices.get(input)
          : input.defaultValue;
        if (input.value === previousValue) return;
        submittedPrices.set(input, input.value);
      }
      track("filter_used", { filter_name: input.name, filter_value: input.value });
      form.requestSubmit();
    };
    document.addEventListener("change", (event) => {
      const input = event.target;
      if (input.matches?.("[data-filter-input]")) submitFilter(input);
      if (input.matches?.("[data-sort-select]")) input.form?.requestSubmit();
    });
    document.addEventListener("focusout", (event) => {
      if (event.target.matches?.('[data-filter-input][type="number"]')) {
        submitFilter(event.target);
      }
    });
  }

  function setupUtilityEvents() {
    const productRoot = document.querySelector("[data-product-root]");
    if (productRoot) {
      track("product_viewed", {
        product_id: productRoot.dataset.productId || "",
        product_handle: productRoot.dataset.productHandle || "",
      });
    }
    document.querySelectorAll("[data-checkout-button]").forEach((button) => {
      button.addEventListener("click", () => track("checkout_started"));
    });
    document.querySelectorAll("[data-bulk-form]").forEach((form) => {
      form.addEventListener("submit", () =>
        track("bulk_order_form_submitted"),
      );
    });
    document.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        track("product_added_to_wishlist", {
          product_id: button.dataset.productId || "",
        });
      });
    });
    document
      .querySelectorAll('form[role="search"]:not([data-predictive-search])')
      .forEach((form) => {
        form.addEventListener("submit", () => {
          const input = form.querySelector('input[name="q"]');
          const query = input?.value?.trim() || "";
          track("search_performed", {
            query_length: Array.from(query).length,
          });
        });
      });
    document.querySelectorAll("[data-share-product]").forEach((button) => {
      button.addEventListener("click", async () => {
        const data = {
          title: document.title,
          text: document.title,
          url: window.location.href,
        };
        try {
          if (navigator.share) await navigator.share(data);
          else {
            await navigator.clipboard.writeText(window.location.href);
            announce("Link copied.");
          }
        } catch (_error) {
          // User cancellation or clipboard restrictions need no error surface.
        }
      });
    });
    document.addEventListener("court-current:purchase-completed", (event) => {
      track("purchase_completed", event.detail || {});
    });
  }

  setupNavigation();
  setupDrawers();
  setupPredictiveSearch();
  setupProductForms();
  setupProductGallery();
  setupQuantityControls();
  setupCart();
  setupCompare();
  setupFinder();
  setupGuideToc();
  setupRecentlyViewed();
  function setupStickyHeader() {
    const header = document.querySelector("[data-site-header]");
    if (!header) return;

    // A zero-height sentinel above the header is cheaper than a scroll
    // listener and stays correct when the announcement bar is toggled.
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;top:0;height:1px;width:100%;";
    header.parentNode.insertBefore(sentinel, header);

    new IntersectionObserver(
      ([entry]) => {
        header.classList.toggle("is-stuck", !entry.isIntersecting);
      },
      { rootMargin: "0px" },
    ).observe(sentinel);
  }

  function setupStickyAtc() {
    const bar = document.querySelector("[data-sticky-atc]");
    const anchor = document.querySelector("[data-add-to-cart]");
    if (!bar || !anchor) return;
    bar.classList.remove("is-visible");
    bar.inert = true;
    bar.setAttribute("aria-hidden", "true");

    // Reveal the bar only once the real add-to-cart has scrolled away, so it
    // never competes with the button it is standing in for.
    let visible = false;
    let scheduled = false;
    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const nextVisible = rect.height > 0 && rect.bottom <= 0;
      if (nextVisible === visible) return;
      visible = nextVisible;
      bar.classList.toggle("is-visible", visible);
      bar.inert = !visible;
      bar.setAttribute("aria-hidden", String(!visible));
    };
    const scheduleUpdate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        update();
      });
    };
    // A large scroll can skip from below to above the viewport without an
    // intersection change. Measure on scroll too, once per animation frame.
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    new IntersectionObserver(scheduleUpdate).observe(anchor);
    update();
  }

  function setupPaddleMatcher() {
    const root = document.querySelector("[data-matcher-root]");
    if (!root) return;
    const grid = document.querySelector("[data-collection-root] .product-grid");
    if (!grid) return;

    const AXES = ["power", "control", "spin", "forgiveness", "maneuverability"];
    // 1..10 on each axis, so the worst possible distance is 9 per axis.
    const WORST = AXES.length * 9;

    const cards = [...grid.querySelectorAll("[data-product-card]")].map((el) => {
      const ratings = {};
      let rated = true;
      AXES.forEach((axis) => {
        const raw = parseInt(el.dataset[`rating${axis[0].toUpperCase()}${axis.slice(1)}`], 10);
        if (Number.isNaN(raw)) rated = false;
        ratings[axis] = raw;
      });
      return { el, ratings, rated, order: [...grid.children].indexOf(el) };
    });

    // Nothing to rank against unless at least two cards carry a full rating set.
    if (cards.filter((c) => c.rated).length < 2) return;
    root.hidden = false;

    const badgeFor = (card) => {
      let badge = card.el.querySelector("[data-matcher-badge]");
      if (!badge) {
        badge = document.createElement("p");
        badge.className = "product-card__match";
        badge.setAttribute("data-matcher-badge", "");
        const body = card.el.querySelector(".product-card__body") || card.el;
        body.prepend(badge);
      }
      return badge;
    };

    const targets = Object.fromEntries(AXES.map((a) => [a, 5]));
    let touched = false;

    function apply() {
      cards.forEach((card) => {
        if (!card.rated) {
          card.score = -1;
          return;
        }
        const distance = AXES.reduce(
          (sum, axis) => sum + Math.abs(targets[axis] - card.ratings[axis]),
          0,
        );
        card.score = Math.round((1 - distance / WORST) * 100);
      });

      const ranked = [...cards].sort((a, b) =>
        b.score === a.score ? a.order - b.order : b.score - a.score,
      );

      const frag = document.createDocumentFragment();
      ranked.forEach((card) => {
        if (touched) {
          const badge = badgeFor(card);
          badge.textContent = card.rated ? `${card.score}% match` : "Not rated";
          badge.classList.toggle("is-top", touched && card.rated && card === ranked[0]);
        }
        frag.appendChild(card.el);
      });
      grid.appendChild(frag);

      const status = root.querySelector("[data-matcher-status]");
      if (status && touched) {
        const best = ranked.find((c) => c.rated);
        const title = best?.el.querySelector(".product-card__title, h3, h2");
        status.textContent = best
          ? `Best match: ${title ? title.textContent.trim() : "top result"} at ${best.score}%.`
          : "";
      }
    }

    root.addEventListener("input", (event) => {
      const slider = event.target.closest("[data-matcher-slider]");
      if (!slider) return;
      const axis = slider.dataset.matcherSlider;
      targets[axis] = parseInt(slider.value, 10);
      const out = root.querySelector(`[data-matcher-value="${axis}"]`);
      if (out) out.textContent = slider.value;
      if (!touched) {
        touched = true;
        root.querySelector("[data-matcher-reset]")?.removeAttribute("hidden");
        track("paddle_matcher_used", { axis });
      }
      apply();
    });

    root.querySelector("[data-matcher-reset]")?.addEventListener("click", () => {
      AXES.forEach((axis) => {
        targets[axis] = 5;
        const slider = root.querySelector(`[data-matcher-slider="${axis}"]`);
        const out = root.querySelector(`[data-matcher-value="${axis}"]`);
        if (slider) slider.value = "5";
        if (out) out.textContent = "5";
      });
      touched = false;
      cards.forEach((card) => card.el.querySelector("[data-matcher-badge]")?.remove());
      const status = root.querySelector("[data-matcher-status]");
      if (status) status.textContent = "";
      root.querySelector("[data-matcher-reset]")?.setAttribute("hidden", "");
      apply();
    });

    // The homepage teaser hands its targets over in the query string. Arriving
    // with them set is treated exactly like having moved the sliders here, so
    // the grid is already ranked on first paint.
    const params = new URLSearchParams(window.location.search);
    let preset = false;
    AXES.forEach((axis) => {
      const raw = parseInt(params.get(axis), 10);
      if (Number.isNaN(raw) || raw < 1 || raw > 10) return;
      targets[axis] = raw;
      preset = true;
      const slider = root.querySelector(`[data-matcher-slider="${axis}"]`);
      const out = root.querySelector(`[data-matcher-value="${axis}"]`);
      if (slider) slider.value = String(raw);
      if (out) out.textContent = String(raw);
    });
    if (preset) {
      touched = true;
      root.querySelector("[data-matcher-reset]")?.removeAttribute("hidden");
      apply();
    }
  }

  /* Homepage teaser: collects the same five targets and rewrites its CTA so the
     link carries them to the collection. Never reorders anything itself. */
  function setupMatcherTeaser() {
    const root = document.querySelector("[data-teaser-root]");
    const cta = document.querySelector("[data-teaser-cta]");
    if (!root || !cta) return;

    const base = cta.dataset.teaserBase || cta.getAttribute("href");
    const targets = {};
    let touched = false;

    function sync() {
      const params = new URLSearchParams();
      Object.keys(targets).forEach((axis) => params.set(axis, targets[axis]));
      const query = params.toString();
      cta.setAttribute("href", query ? `${base}?${query}` : base);
    }

    root.addEventListener("input", (event) => {
      const slider = event.target.closest("[data-teaser-slider]");
      if (!slider) return;
      const axis = slider.dataset.teaserSlider;
      targets[axis] = parseInt(slider.value, 10);
      const out = root.querySelector(`[data-teaser-value="${axis}"]`);
      if (out) out.textContent = slider.value;
      if (!touched) {
        touched = true;
        track("paddle_matcher_teaser_used", { axis });
      }
      sync();
    });
  }

  setupFiltersAndSort();
  setupUtilityEvents();
  setupStickyHeader();
  setupStickyAtc();
  setupPaddleMatcher();
  setupMatcherTeaser();
})();
